import React from "react"
import PropTypes from "prop-types"

import Form from "./form"
import FormField from "./form_field"

import * as $ from "jquery"

/**
 * This form is used to build a URL for starting the embed flow, for debugging
 * purposes.
 */
export default class FlowUrlConstructor extends React.Component {
  static propTypes = {
    baseUrl: PropTypes.string.isRequired,
    connectedUserId: PropTypes.string,
    currentLogin: PropTypes.string,
    owningChannelModuleName: PropTypes.string.isRequired,
    owningChannelName: PropTypes.string.isRequired,
    userEmail: PropTypes.string.isRequired,
    validRedirectUri: PropTypes.string,
  }

  state = {
    userId: this.props.connectedUserId || "",
    includeEmail: false,
    includeRedirectUri: false,
  }

  generateUrl() {
    const { baseUrl, userEmail, validRedirectUri } = this.props
    const { userId, includeEmail, includeRedirectUri } = this.state

    const params = {}
    if (userId) params.user_id = userId
    if (includeEmail) params.email = userEmail
    if (includeRedirectUri) params.redirect_uri = validRedirectUri

    return `${baseUrl}?${$.param(params)}`
  }

  generateSteps() {
    const {
      connectedUserId,
      currentLogin,
      owningChannelName,
      userEmail,
    } = this.props

    const { includeEmail, includeRedirectUri, userId } = this.state

    const passingUnknownUserId = userId && userId !== connectedUserId

    const loggedIn = []
    const loggedOut = []

    if (passingUnknownUserId) {
      ;[loggedIn, loggedOut].forEach(list => {
        list.push(
          <li>
            If an IFTTT account exists that’s connected to the{" "}
            {owningChannelName} account with the id <code>{userId}</code>,
            prompt you to log into it
          </li>
        )
        list.push(
          <li>
            If no IFTTT account exists that’s connected to that{" "}
            {owningChannelName} account, prompt you to sign up or log in with
            any IFTTT account
          </li>
        )
      })
    } else if (userId) {
      loggedIn.push(
        <li>Confirm that you’re logged into the correct IFTTT account</li>
      )
      loggedOut.push(
        <li>
          Prompt you to log into the IFTTT account with the login{" "}
          <b>{currentLogin}</b>
        </li>
      )
    } else {
      loggedIn.push(
        <li>Confirm that you’re logged into an acceptable IFTTT account</li>
      )
      loggedOut.push(
        <li>Prompt you to sign up or log in with any IFTTT account</li>
      )
    }

    if (includeEmail) {
      if (passingUnknownUserId) {
        loggedIn.push(
          <li>
            Prefill the sign in and sign up forms with the email {userEmail}
          </li>
        )
      }
      loggedOut.push(
        <li>
          Prefill the sign in and sign up forms with the email {userEmail}
        </li>
      )
    }

    ;[loggedIn, loggedOut].forEach(list => {
      list.push(
        <li>
          Ask you to connect the {owningChannelName} service if you haven’t
          already
        </li>
      )
    })

    if (includeRedirectUri) {
      ;[loggedIn, loggedOut].forEach(list => {
        list.push(<li>Redirect you after enabling the applet</li>)
      })
    }

    // Add keys to the items here so we don't have to do it earlier
    return {
      loggedIn: loggedIn.map((li, i) => React.cloneElement(li, { key: i })),
      loggedOut: loggedOut.map((li, i) => React.cloneElement(li, { key: i })),
    }
  }

  render() {
    const {
      connectedUserId,
      currentLogin,
      owningChannelModuleName,
      owningChannelName,
      userEmail,
      validRedirectUri,
    } = this.props
    const { includeEmail, includeRedirectUri, userId } = this.state

    const url = this.generateUrl()
    const steps = this.generateSteps()

    return (
      <Form>
        <FormField
          label="user_id"
          onChange={e => this.setState({ userId: e.target.value })}
          value={userId}
        />

        <RadioButtonField
          id="redirect_uri"
          label="redirect_uri"
          value={includeRedirectUri}
          onChange={value => this.setState({ includeRedirectUri: value })}
          options={[
            { label: "Don’t include", value: false },
            { label: "Include", value: true, disabled: !validRedirectUri },
          ]}
        />

        <RadioButtonField
          id="email"
          label="email"
          value={includeEmail}
          onChange={value => this.setState({ includeEmail: value })}
          options={[
            { label: "Don’t include", value: false },
            { label: "Include", value: true, disabled: !userEmail },
          ]}
        />

        <FormField label="URL">
          <a href={url}>{url}</a>
        </FormField>

        <p>Visiting this URL in your current browser will:</p>

        {currentLogin ? (
          <div>
            <ul className="list-with-bullets">{steps.loggedIn}</ul>

            <p>Visiting this URL in a logged-out browser will:</p>

            <ul className="list-with-bullets">{steps.loggedOut}</ul>
          </div>
        ) : (
          <ul className="list-with-bullets">{steps.loggedOut}</ul>
        )}

        {connectedUserId ? (
          <p>
            To see the flow in the case where the user has never connected their{" "}
            {owningChannelName} account to IFTTT before, first{" "}
            <a
              data-confirm={`
                This will disconnect Uber and permanently delete all
                of your Uber Applets. Are you sure you want to delete your
                ${owningChannelName} Applets and disconnect
                ${owningChannelName}?
              `}
              data-method="delete"
              href={`/services/${owningChannelModuleName}/settings`}
            >
              disconnect the {owningChannelName} service
            </a>{" "}
            (potentially copying your user id from this page first).
          </p>
        ) : (
          <p>
            To see the flow in the case where the user has previously connected{" "}
            their {owningChannelName} account to IFTTT, first{" "}
            <a href={`/${owningChannelModuleName}`}>
              connect the {owningChannelName} service
            </a>
            .
          </p>
        )}
      </Form>
    )
  }
}

class RadioButtonField extends React.Component {
  static propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.any.isRequired,
        disabled: PropTypes.bool,
      }).isRequired
    ).isRequired,
  }

  render() {
    const { id, label, value, onChange, options } = this.props

    return (
      <FormField label={label}>
        {options.map((option, idx) => (
          <p key={idx}>
            <input
              type="radio"
              id={`${id}-${option.value}-input`}
              checked={value === option.value}
              disabled={option.disabled}
              onChange={e => e.target.value === "on" && onChange(option.value)}
            />
            <label htmlFor={`${id}-${option.value}-input`}>
              {option.label}
            </label>
          </p>
        ))}
      </FormField>
    )
  }
}
